import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname, parseError } from "../../../utils";
import {
  INTEGRATION_SHARING_SHARED,
  INTEGRATION_SHARING_PRIVATE,
} from "../../../constants";
import DepartmentSelector from "../../../components/DepartmentSelector";

interface IntegrationSharingDialogProps {
  open: boolean;
  onClose: () => void;
  integration: any;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: Function;
  onSaved?: () => void;
}

const IntegrationSharingDialog: React.FC<IntegrationSharingDialogProps> = ({
  open,
  onClose,
  integration,
  companyId,
  getToken,
  eventListener,
  onSaved,
}) => {
  const [sharing, setSharing] = useState(INTEGRATION_SHARING_PRIVATE);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (integration) {
      setSharing(integration.sharing || INTEGRATION_SHARING_PRIVATE);
      // departmentIds from backend is array of strings or null
      setDepartmentIds(integration.departmentIds || []);
    }
  }, [integration]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentToken = await getToken();
      await axios.put(
        `${hostname}/integrations/${integration.id}/sharing`,
        {
          companyId,
          sharing,
          departmentIds:
            sharing === INTEGRATION_SHARING_SHARED && departmentIds.length > 0
              ? departmentIds
              : null,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      eventListener({
        type: "SET_SUCCESS",
        message: "Sharing settings updated successfully",
      });
      if (onSaved) {
        onSaved();
      }
      onClose();
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!integration) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Sharing: {integration.instanceName || integration.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={sharing === INTEGRATION_SHARING_SHARED}
                onChange={() =>
                  setSharing(
                    sharing === INTEGRATION_SHARING_SHARED
                      ? INTEGRATION_SHARING_PRIVATE
                      : INTEGRATION_SHARING_SHARED
                  )
                }
              />
            }
            label={
              <Box>
                <Typography>
                  {sharing === INTEGRATION_SHARING_SHARED ? "Shared" : "Private"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {sharing === INTEGRATION_SHARING_SHARED
                    ? "Other team members can see and use this integration"
                    : "Only you can see and use this integration"}
                </Typography>
              </Box>
            }
          />
        </Box>

        {sharing === INTEGRATION_SHARING_SHARED && (
          <Box sx={{ mt: 3 }}>
            <DepartmentSelector
              companyId={companyId}
              getToken={getToken}
              selectedDepartmentIds={departmentIds}
              onChange={setDepartmentIds}
              eventListener={eventListener}
              label="Departments (Optional)"
              helperText="If departments are selected, only members assigned to those departments can see this integration. Leave empty for all departments."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntegrationSharingDialog;
